import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import {
    ConcessionAiGenerateInput,
    ConcessionAiGenerateResponse,
    ConcessionMergeVariable,
    ConcessionMessage,
    ConcessionMessagePreview,
    ConcessionMessageRecipientsResponse,
    CreateConcessionMessageInput,
    UpdateConcessionMessageInput,
} from '../models/Concession'

const MESSAGES_KEY = 'concession-messages'
const BASE = '/beta/api/concession/messages'

const extractError = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError)?.response?.data as
        | Record<string, unknown>
        | undefined

    if (data) {
        const nonField = data.non_field_errors
        if (Array.isArray(nonField) && nonField.length > 0) {
            return String(nonField[0])
        }
        const firstKey = Object.keys(data)[0]
        if (firstKey) {
            const val = data[firstKey]
            if (Array.isArray(val) && val.length > 0) {
                return `${firstKey}: ${val[0]}`
            }
            if (typeof val === 'string') {
                return val
            }
        }
    }

    return fallback
}

export const useConcessionMessages = (concessionId?: number) => {
    const queryClient = useQueryClient()

    const { data: messages, isLoading, error, refetch } = useQuery<
        ConcessionMessage[],
        Error
    >({
        queryKey: [MESSAGES_KEY, concessionId],
        queryFn: async () => {
            const res = await axiosInstance.get<ConcessionMessage[]>(
                `${BASE}/?concession=${concessionId}`,
            )
            return res.data
        },
        enabled: !!concessionId,
        refetchOnWindowFocus: false,
    })

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] })

    const createMessage = useMutation({
        mutationFn: async (body: CreateConcessionMessageInput) => {
            const res = await axiosInstance.post<ConcessionMessage>(
                `${BASE}/`,
                body,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Message scheduled')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to schedule message'))
        },
    })

    const updateMessage = useMutation({
        mutationFn: async ({
            id,
            body,
        }: {
            id: number
            body: UpdateConcessionMessageInput
        }) => {
            const res = await axiosInstance.patch<ConcessionMessage>(
                `${BASE}/${id}/`,
                body,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Message updated')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to update message'))
        },
    })

    const deleteMessage = useMutation({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`${BASE}/${id}/`)
        },
        onSuccess: () => {
            invalidate()
            toast.success('Message deleted')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to delete message'))
        },
    })

    return {
        messages: messages ?? [],
        isLoading,
        error,
        refetch,
        createMessage,
        updateMessage,
        deleteMessage,
    }
}

/** Preview the tenants who would currently receive a scheduled message. */
export const fetchMessageRecipients = async (
    messageId: number,
): Promise<ConcessionMessageRecipientsResponse> => {
    const res = await axiosInstance.get<ConcessionMessageRecipientsResponse>(
        `${BASE}/${messageId}/recipients/`,
    )
    return res.data
}

const MERGE_VARIABLES_KEY = 'concession-merge-variables'

/** Available `{{merge}}` tags for personalizing message copy. */
export const useConcessionMergeVariables = () => {
    const { data, isLoading } = useQuery<ConcessionMergeVariable[], Error>({
        queryKey: [MERGE_VARIABLES_KEY],
        queryFn: async () => {
            const res = await axiosInstance.get<ConcessionMergeVariable[]>(
                `${BASE}/merge-variables/`,
            )
            return res.data
        },
        staleTime: 60 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    return { mergeVariables: data ?? [], isLoadingMergeVariables: isLoading }
}

/** Resolve a saved message's tags for a real recipient. */
export const fetchMessagePreview = async (
    messageId: number,
    leaseId?: number,
): Promise<ConcessionMessagePreview> => {
    const query = leaseId ? `?lease=${leaseId}` : ''
    const res = await axiosInstance.get<ConcessionMessagePreview>(
        `${BASE}/${messageId}/preview/${query}`,
    )
    return res.data
}

/** Generate a starting subject/body draft with AI (nothing is persisted). */
export const generateAiDraft = async (
    body: ConcessionAiGenerateInput,
): Promise<ConcessionAiGenerateResponse> => {
    const res = await axiosInstance.post<ConcessionAiGenerateResponse>(
        `${BASE}/ai-generate/`,
        body,
    )
    return res.data
}
